import React, { useState, useEffect } from 'react';
import { BookOpen, Calendar, Clock, CheckCircle, FileText, User, Loader, Filter, Search } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import { showErrorNotification } from '../../utils/errorHandler';

const LearnerDailyLearnings = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [lessonPlans, setLessonPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDomain, setFilterDomain] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadLessonPlans();
        }
    }, [selectedChild]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadLessonPlans = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);
            // This would need to be implemented in the backend
            // For now, we'll use mock data based on the student's grade
            const mockLessonPlans = [
                {
                    id: '1',
                    title: 'Introduction to Numbers',
                    domain: 'Mathematics',
                    date: '2024-01-15',
                    status: 'Completed',
                    teacher: 'Ms. Johnson',
                    description: 'Basic number recognition and counting exercises',
                    competencies: ['Number Recognition', 'Counting Skills'],
                    duration: '45 minutes'
                },
                {
                    id: '2',
                    title: 'Story Reading Session',
                    domain: 'Language Arts',
                    date: '2024-01-14',
                    status: 'Completed',
                    teacher: 'Ms. Smith',
                    description: 'Interactive story reading with comprehension questions',
                    competencies: ['Reading Comprehension', 'Vocabulary Building'],
                    duration: '30 minutes'
                },
                {
                    id: '3',
                    title: 'Nature Walk and Observation',
                    domain: 'Science',
                    date: '2024-01-13',
                    status: 'Completed',
                    teacher: 'Mr. Brown',
                    description: 'Outdoor learning about plants and animals',
                    competencies: ['Observation Skills', 'Environmental Awareness'],
                    duration: '60 minutes'
                },
                {
                    id: '4',
                    title: 'Creative Art Expression',
                    domain: 'Arts',
                    date: '2024-01-12',
                    status: 'In Progress',
                    teacher: 'Ms. Davis',
                    description: 'Painting and drawing activities to express creativity',
                    competencies: ['Creative Expression', 'Fine Motor Skills'],
                    duration: '40 minutes'
                },
                {
                    id: '5',
                    title: 'Physical Exercise Games',
                    domain: 'Physical Education',
                    date: '2024-01-11',
                    status: 'Completed',
                    teacher: 'Coach Wilson',
                    description: 'Fun physical activities and coordination games',
                    competencies: ['Gross Motor Skills', 'Coordination'],
                    duration: '50 minutes'
                }
            ];

            setLessonPlans(mockLessonPlans);
        } catch (err) {
            console.error('Error loading lesson plans:', err);
            showErrorNotification('Failed to load lesson plans');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'in progress':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'upcoming':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getDomainColor = (domain) => {
        const colors = {
            'Mathematics': 'bg-blue-500',
            'Language Arts': 'bg-green-500',
            'Science': 'bg-purple-500',
            'Arts': 'bg-pink-500',
            'Physical Education': 'bg-orange-500',
            'Social Studies': 'bg-indigo-500'
        };
        return colors[domain] || 'bg-gray-500';
    };

    const filteredLessonPlans = lessonPlans.filter(lesson => {
        const matchesSearch = lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            lesson.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDomain = filterDomain === 'all' || lesson.domain === filterDomain;
        const matchesStatus = filterStatus === 'all' || lesson.status.toLowerCase() === filterStatus.toLowerCase();
        
        return matchesSearch && matchesDomain && matchesStatus;
    });

    const domains = [...new Set(lessonPlans.map(lesson => lesson.domain))];

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Daily Learnings">
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Daily Learnings</h2>
                        <p className="text-gray-600">Please wait while we fetch learning activities...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild) {
        return (
            <Layout pageTitle="Learner's Daily Learnings">
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Child Selected</h2>
                        <p className="text-gray-600">Please select a child to view their daily learnings.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Daily Learnings">
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Daily Learning Activities</h1>
                                <p className="text-gray-600">Track {selectedChild?.name}'s taught lesson plans and learning progress</p>
                            </div>
                            
                            {/* Child Selector */}
                            {children.length > 1 && (
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                                    >
                                        {children.map(child => (
                                            <option key={child.student_id} value={child.student_id}>
                                                {child.name} - Grade {child.grade || 'N/A'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap gap-4 items-center">
                            {/* Search */}
                            <div className="relative flex-1 min-w-64">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search lessons..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Domain Filter */}
                            <div className="relative">
                                <select
                                    value={filterDomain}
                                    onChange={(e) => setFilterDomain(e.target.value)}
                                    className="bg-white border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer pr-10"
                                >
                                    <option value="all">All Domains</option>
                                    {domains.map(domain => (
                                        <option key={domain} value={domain}>{domain}</option>
                                    ))}
                                </select>
                                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>

                            {/* Status Filter */}
                            <div className="relative">
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="bg-white border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer pr-10"
                                >
                                    <option value="all">All Status</option>
                                    <option value="completed">Completed</option>
                                    <option value="in progress">In Progress</option>
                                    <option value="upcoming">Upcoming</option>
                                </select>
                                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Lesson Plans Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredLessonPlans.length > 0 ? (
                            filteredLessonPlans.map((lesson) => (
                                <div key={lesson.id} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 ${getDomainColor(lesson.domain)} rounded-xl flex items-center justify-center`}>
                                                <BookOpen className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800 line-clamp-2">{lesson.title}</h3>
                                                <p className="text-sm text-gray-600">{lesson.domain}</p>
                                            </div>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(lesson.status)}`}>
                                            {lesson.status}
                                        </span>
                                    </div>

                                    {/* Description */}
                                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{lesson.description}</p>

                                    {/* Details */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Calendar className="w-4 h-4" />
                                            <span>{new Date(lesson.date).toLocaleDateString()}</span>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Clock className="w-4 h-4" />
                                            <span>{lesson.duration}</span>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <User className="w-4 h-4" />
                                            <span>{lesson.teacher}</span>
                                        </div>
                                    </div>

                                    {/* Competencies */}
                                    <div className="mt-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Competencies Covered:</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {lesson.competencies.map((competency, index) => (
                                                <span 
                                                    key={index}
                                                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                                >
                                                    {competency}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <button className="w-full mt-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105">
                                        View Details
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center py-12">
                                <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                <h3 className="text-xl font-bold text-gray-600 mb-2">No Learning Activities Found</h3>
                                <p className="text-gray-500">
                                    {searchTerm || filterDomain !== 'all' || filterStatus !== 'all' 
                                        ? 'Try adjusting your search or filter criteria.'
                                        : 'No learning activities have been recorded yet.'
                                    }
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Summary Stats */}
                    {filteredLessonPlans.length > 0 && (
                        <div className="mt-8 bg-white rounded-2xl shadow-xl p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Learning Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="text-center p-4 bg-blue-50 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-600">{filteredLessonPlans.length}</div>
                                    <div className="text-sm text-gray-600">Total Activities</div>
                                </div>
                                <div className="text-center p-4 bg-green-50 rounded-lg">
                                    <div className="text-2xl font-bold text-green-600">
                                        {filteredLessonPlans.filter(l => l.status === 'Completed').length}
                                    </div>
                                    <div className="text-sm text-gray-600">Completed</div>
                                </div>
                                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                                    <div className="text-2xl font-bold text-yellow-600">
                                        {filteredLessonPlans.filter(l => l.status === 'In Progress').length}
                                    </div>
                                    <div className="text-sm text-gray-600">In Progress</div>
                                </div>
                                <div className="text-center p-4 bg-purple-50 rounded-lg">
                                    <div className="text-2xl font-bold text-purple-600">{domains.length}</div>
                                    <div className="text-sm text-gray-600">Domains Covered</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default LearnerDailyLearnings;